import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { seoPlanApi, type ActionStatus } from './api'

const KEY = ['seo-plan'] as const

export function useSeoPlanSites() {
  return useQuery({
    queryKey: [...KEY, 'sites'],
    queryFn: seoPlanApi.listSites,
    // Poll faster while a run is going, so it shows up when it finishes.
    refetchInterval: (query) =>
      query.state.data?.some((s) => s.last_run?.status === 'running') ? 5000 : 60000,
  })
}

export function useSeoPlanSite(dossierId: number) {
  return useQuery({
    queryKey: [...KEY, 'site', dossierId],
    queryFn: () => seoPlanApi.getSite(dossierId),
    refetchInterval: (query) => (query.state.data?.last_run?.status === 'running' ? 5000 : false),
  })
}

export function useSeoPlanPages(dossierId: number, enabled: boolean) {
  return useQuery({
    queryKey: [...KEY, 'pages', dossierId],
    queryFn: () => seoPlanApi.getPages(dossierId),
    enabled,
  })
}

export function useSeoPlanQueries(dossierId: number, enabled: boolean) {
  return useQuery({
    queryKey: [...KEY, 'queries', dossierId],
    queryFn: () => seoPlanApi.getQueries(dossierId),
    enabled,
  })
}

export function useSeoPlanInstruction(dossierId: number, enabled: boolean) {
  return useQuery({
    queryKey: [...KEY, 'instruction', dossierId],
    queryFn: () => seoPlanApi.getInstruction(dossierId),
    enabled,
  })
}

/** Starts a run and polls it until it is done or failed. */
export function useStartSeoPlanRun() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (dossierId: number) => {
      const run = await seoPlanApi.startRun(dossierId)
      queryClient.invalidateQueries({ queryKey: KEY })
      // 200 pages at 2 per second is under two minutes; give up after ten.
      for (let i = 0; i < 200; i++) {
        await new Promise((resolve) => setTimeout(resolve, 3000))
        const current = await seoPlanApi.getRun(run.id)
        if (current.status === 'done') return current
        if (current.status === 'failed') throw new Error(current.error ?? 'Analyse mislukt')
      }
      throw new Error('De analyse duurt langer dan verwacht; kijk straks opnieuw.')
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: KEY }),
  })
}

export function useStartAllSeoPlanRuns() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: seoPlanApi.startAll,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  })
}

export function useUpdateSeoPlanAction() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: Exclude<ActionStatus, 'resolved'> }) =>
      seoPlanApi.updateAction(id, status),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  })
}
